/*
  FreshFit silk pillowcase: bundle tiles + mix-and-match set builder (Zilk-style), gallery slider, delivery timeline,
  sticky bar and add to cart.

  One shared state per page. Any section can bind to it with data attributes:
    data-silk-text="key"      text is kept in sync (counter, cta, heroCta, each1 …)
    data-silk-pick="1|2|4"    selects a bundle (tiles, the "Get 4" button)
    data-silk-atc             adds the set to the cart when it's full, otherwise points to the empty slots
    data-silk-scroll="sel"    scrolls to an element (buttons outside the buy box go back to the bundle tiles)
  The set starts empty. Each bundle remembers its own picks for the browser session; a bundle you open for the
  first time starts from what you've already picked.
  Cart: the set is added as single-unit line items (Size × Color variants). Bundle pricing comes from the store's
  automatic discounts; the prices shown here mirror them (section settings "Pair discount" / "Free item").
*/
(() => {
  // several sections include this file; run it once
  if (window.__silkLoaded) return;
  window.__silkLoaded = true;

  const qs = (s, r = document) => r.querySelector(s);
  const qsa = (s, r = document) => Array.from(r.querySelectorAll(s));
  const fill = (tpl, map) => String(tpl || '').replace(/\[(\w+)\]/g, (m, k) => (k in map ? map[k] : m));
  const esc = (t) => String(t).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  class SilkPDP {
    constructor(cfg) {
      this.cfg = cfg;
      this.t = cfg.text || {};
      const firstSize = cfg.sizes[0] ? cfg.sizes[0].value : '';
      this.key = 'silk-set:' + (cfg.product || '');
      this.state = {
        pack: cfg.packs.indexOf(cfg.defaultPack) > -1 ? cfg.defaultPack : 2,
        lists: { 1: null, 2: null, 4: null },
        nextSize: firstSize,
        last: cfg.colors[0] && cfg.colors[0].value,
        note: '',
      };
      // optional preselected colors (theme setting; empty by default)
      const pre = (cfg.defaultColors || [])
        .filter((c) => this.color(c))
        .slice(0, this.state.pack)
        .map((c) => ({ size: firstSize, color: c }));
      if (pre.length) {
        this.state.lists[this.state.pack] = pre;
        this.state.last = pre[pre.length - 1].color;
      }
      this.restore();
      this.fromUrl();
      this.icons = {};
      qsa('template[data-silk-icon]').forEach((t) => (this.icons[t.dataset.silkIcon] = t.innerHTML.trim()));
      this.bind();
      this.render();
      this.initGalleries();
      this.initSticky();
      this.initDelivery();
      this.initFloatAtc();
    }

    // ---------------------------------------------------------------- data
    color(value) {
      return this.cfg.colors.find((c) => c.value === value);
    }
    size(value) {
      return this.cfg.sizes.find((s) => s.value === value);
    }
    variant(size, color) {
      return this.cfg.variants.find((v) => v.size === size && v.color === color);
    }
    unitPrice(size) {
      const list = this.cfg.variants.filter((v) => v.size === size);
      const pick = list.find((v) => v.available) || list[0];
      return pick ? pick.price : 0;
    }
    itemPrice(item) {
      const v = this.variant(item.size, item.color);
      return v ? v.price : this.unitPrice(item.size);
    }
    // total for k pillowcases; empty slots are priced at the size picked for the next pick
    priceOf(items, k) {
      const base = items.slice(0, k).map((i) => this.itemPrice(i));
      while (base.length < k) base.push(this.unitPrice(this.state.nextSize));
      const full = base.reduce((a, b) => a + b, 0);
      const d = this.cfg.discount || {};
      let total = full;
      if (d.perItem > 0 && k >= d.minQty) total = Math.min(total, full - d.perItem * k);
      if (d.freeEvery > 0 && k >= d.freeEvery) {
        const free = Math.floor(k / d.freeEvery);
        const cheapest = base.slice().sort((a, b) => a - b).slice(0, free);
        total = Math.min(total, full - cheapest.reduce((a, b) => a + b, 0));
      }
      return { full, total: Math.max(0, total) };
    }
    money(cents) {
      const sym = this.cfg.symbol || '$';
      const v = Math.round(cents) / 100;
      const txt = Number.isInteger(v) ? v.toLocaleString('en-US') : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return sym + txt;
    }

    // ---------------------------------------------------------------- actions
    items() {
      return this.state.lists[this.state.pack] || [];
    }
    setState(patch) {
      Object.assign(this.state, patch);
      this.save();
      this.render();
    }
    save() {
      const s = this.state;
      try {
        sessionStorage.setItem(this.key, JSON.stringify({ pack: s.pack, lists: s.lists, nextSize: s.nextSize, last: s.last }));
      } catch (e) {}
    }
    restore() {
      try {
        const d = JSON.parse(sessionStorage.getItem(this.key) || 'null');
        if (!d) return;
        const ok = (i) => i && this.variant(i.size, i.color);
        this.cfg.packs.forEach((k) => {
          const list = d.lists && d.lists[k];
          this.state.lists[k] = Array.isArray(list) ? list.filter(ok).slice(0, k) : null;
        });
        if (this.cfg.packs.indexOf(d.pack) > -1) this.state.pack = d.pack;
        if (this.size(d.nextSize)) this.state.nextSize = d.nextSize;
        if (this.color(d.last)) this.state.last = d.last;
      } catch (e) {}
    }
    // links from the homepage: ?variant=<id> starts the set with that color (when the set is empty) and shows its
    // photo first; ?pack=1|2|4 selects that bundle
    fromUrl() {
      let q;
      try {
        q = new URLSearchParams(window.location.search);
      } catch (e) {
        return;
      }
      const s = this.state;
      const pack = Number(q.get('pack'));
      if (this.cfg.packs.indexOf(pack) > -1 && pack !== s.pack) {
        if (s.lists[pack] == null) s.lists[pack] = this.items().slice(0, pack);
        s.pack = pack;
      }
      const vid = Number(q.get('variant'));
      const v = vid && this.cfg.variants.find((x) => Number(x.id) === vid);
      if (!v) return;
      s.last = v.color;
      if (v.available && this.items().length === 0) {
        s.lists[s.pack] = [{ size: v.size, color: v.color }];
        s.nextSize = v.size;
      }
      this.save();
    }
    pick(k) {
      const s = this.state;
      // first visit to this bundle: start from the pillowcases already picked
      if (s.lists[k] == null) s.lists[k] = this.items().slice(0, k);
      this.setState({ pack: k, note: '' });
      document.dispatchEvent(new CustomEvent('silk:pack', { detail: { pack: k } }));
    }
    add(colorValue) {
      const s = this.state;
      const v = this.variant(s.nextSize, colorValue);
      if (!v || !v.available) {
        const sz = this.size(s.nextSize);
        this.setState({ note: fill(this.t.soldOut, { color: this.color(colorValue).label, size: sz ? sz.label : s.nextSize }) });
        return;
      }
      const n = s.pack;
      const cur = this.items().slice(0, n);
      const item = { size: s.nextSize, color: colorValue };
      if (cur.length < n) {
        const next = cur.concat([item]);
        s.lists[n] = next;
        this.setState({ last: colorValue, note: next.length === n ? this.t.noteComplete : '' });
      } else if (n < 4 && this.cfg.packs.indexOf(n === 1 ? 2 : 4) > -1) {
        const np = n === 1 ? 2 : 4;
        s.lists[np] = cur.concat([item]);
        this.setState({ pack: np, last: colorValue, note: np === 4 ? this.t.noteUp4 : this.t.noteUp2 });
      } else {
        this.setState({ note: this.t.noteFull });
      }
      this.showSlide('hero');
      document.dispatchEvent(new CustomEvent('silk:item', { detail: item }));
    }
    clearSet() {
      this.cfg.packs.forEach((k) => (this.state.lists[k] = null));
      this.setState({ note: '' });
    }
    remove(i) {
      const cur = this.items().slice(0, this.state.pack);
      cur.splice(i, 1);
      this.state.lists[this.state.pack] = cur;
      this.setState({ note: '' });
    }

    // ---------------------------------------------------------------- values shown on the page
    values() {
      const s = this.state;
      const t = this.t;
      const n = s.pack;
      const items = this.items().slice(0, n);
      const count = items.length;
      const full = count >= n;
      const left = n - count;
      const pr = this.priceOf(items, n);
      const v = {};
      this.cfg.packs.forEach((k) => {
        const p = this.priceOf(s.lists[k] || items.slice(0, k), k);
        v['each' + k] = this.money(Math.round(p.total / k));
        v['save' + k] = this.money(p.full - p.total);
      });
      v.counter = fill(t.counter, { count, total: n });
      v.cta = full ? fill(t.ctaFull, { price: this.money(pr.total) }) : fill(left === 1 ? t.ctaMoreOne : t.ctaMore, { n: left });
      v.installment = this.money(Math.round(pr.total / 4));
      const night = Math.round(pr.total / Math.max(1, n) / 730);
      v.perNight = night < 100 ? night + '¢' : this.money(night);
      v.packName = (t.packNames || {})[n] || String(n);
      const names = [];
      items.forEach((i) => {
        const c = this.color(i.color);
        if (c && names.indexOf(c.label) < 0) names.push(c.label);
      });
      v.stickyLabel = full ? v.packName + ' · ' + names.join(', ') : v.counter;
      v.stickyPrice = this.money(pr.total);
      v.stickyBtn = full ? t.stickyAdd : fill(t.stickyMore, { n: left });
      v.shipNote = n === 1 ? t.singleShip : t.freeShip;
      v.heroCta = n === 1 ? t.heroSingle : t.heroPack;
      v.perks = fill((t.perks || {})[n], { save: this.money(pr.full - pr.total) });
      return { v, full, count, n, items };
    }

    // ---------------------------------------------------------------- rendering
    render() {
      const { v, full, count, n, items } = this.values();
      this.lastValues = v;
      qsa('[data-silk-text]').forEach((el) => {
        const k = el.dataset.silkText;
        if (k in v && el.textContent !== v[k]) el.textContent = v[k];
      });
      qsa('[data-silk-pick]').forEach((el) => el.setAttribute('aria-pressed', String(Number(el.dataset.silkPick) === n)));
      qsa('[data-silk-atc]').forEach((el) => el.setAttribute('aria-disabled', String(!full)));
      qsa('[data-silk-size]').forEach((el) => el.setAttribute('aria-pressed', String(el.dataset.silkSize === this.state.nextSize)));
      qsa('[data-silk-add]').forEach((el) => {
        const variant = this.variant(this.state.nextSize, el.dataset.silkAdd);
        el.setAttribute('aria-disabled', String(!variant || !variant.available));
      });
      qsa('[data-silk-progress]').forEach((el) => {
        el.innerHTML = Array.from({ length: n }, (_, i) => `<span class="${i < count ? 'is-on' : ''}"></span>`).join('');
      });
      qsa('[data-silk-slots]').forEach((el) => {
        el.style.setProperty('--cols', n);
        el.innerHTML = Array.from({ length: n }, (_, i) => (items[i] ? this.slotHTML(items[i], i) : this.emptyHTML(i === count))).join('');
      });
      qsa('[data-silk-note]').forEach((el) => {
        el.hidden = !this.state.note;
        const txt = qs('[data-silk-note-text]', el);
        if (txt) txt.textContent = fill(this.state.note, v);
      });
      this.renderHero();
      this.setFull = full;
      if (this.floatUpdate) this.floatUpdate();
    }
    // Phones, once the set is complete (Single, Pair or 4-Pack): the main add-to-cart button stays on screen. While
    // its place in the page is below the screen it floats at the bottom; while its place is on screen it sits there;
    // once you've scrolled past it, it sticks to the top. A spacer holds its place while it floats. Until the set is
    // complete, and on desktop, it stays in its place.
    initFloatAtc() {
      const btn = qs('[data-silk-atc="main"]');
      if (!btn) return;
      const phone = window.matchMedia('(max-width: 989px)');
      const gap = 12;
      const spacer = document.createElement('span');
      spacer.className = 'silk-atc-spacer';
      spacer.setAttribute('aria-hidden', 'true');
      spacer.hidden = true;
      btn.before(spacer);
      let mode = 'place';
      const set = (next, r) => {
        if (next === 'place') {
          if (mode !== 'place') {
            btn.classList.remove('is-floating', 'is-floating-top', 'is-floating-bottom');
            btn.style.removeProperty('--float-left');
            btn.style.removeProperty('--float-width');
            spacer.hidden = true;
          }
        } else {
          if (mode === 'place') {
            spacer.style.height = `${btn.offsetHeight}px`;
            spacer.hidden = false;
          }
          btn.style.setProperty('--float-left', `${r.left}px`);
          btn.style.setProperty('--float-width', `${r.width}px`);
          btn.classList.add('is-floating');
          btn.classList.toggle('is-floating-top', next === 'top');
          btn.classList.toggle('is-floating-bottom', next === 'bottom');
        }
        mode = next;
      };
      const update = () => {
        if (!phone.matches || !this.setFull || !btn.getClientRects().length) { set('place'); return; }
        // where the button would be in the page
        const r = (mode === 'place' ? btn : spacer).getBoundingClientRect();
        const h = mode === 'place' ? r.height : btn.offsetHeight;
        let next = 'place';
        if (r.top + h > window.innerHeight - gap) next = 'bottom';
        else if (r.top < gap) next = 'top';
        set(next, r);
      };
      let raf = 0;
      const queue = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(update); };
      window.addEventListener('scroll', queue, { passive: true });
      window.addEventListener('resize', queue);
      if (phone.addEventListener) phone.addEventListener('change', queue);
      // the buy box can change height (notes, slots), which moves the button's place
      if ('ResizeObserver' in window) new ResizeObserver(queue).observe(document.body);
      this.floatUpdate = queue;
      update();
    }
    slotHTML(item, i) {
      const c = this.color(item.color) || { label: item.color, hex: '#e7e4dd' };
      const sz = this.size(item.size) || { short: item.size };
      return (
        `<div class="silk-slot silk-slot--filled"><span class="silk-slot__top"><span class="silk-slot__dot" style="background:${esc(c.hex)}"></span>` +
        `<button type="button" class="silk-slot__x" data-silk-remove="${i}" aria-label="${esc(fill(this.t.remove, { color: c.label, size: sz.short }))}">${this.icons.x || '×'}</button></span>` +
        `<span class="silk-slot__color">${esc(c.label)}</span><span class="silk-slot__size">${esc(sz.short)}</span></div>`
      );
    }
    emptyHTML(isNext) {
      return `<div class="silk-slot silk-slot--empty${isNext ? ' silk-slot--next' : ''}">${this.icons.plus || '+'}<span class="silk-slot__label">${esc(this.t.emptySlot)}</span></div>`;
    }
    renderHero() {
      const c = this.color(this.state.last);
      if (!c || !c.image) return;
      qsa('[data-silk-hero] img').forEach((img) => {
        if (img.dataset.color === c.value) return;
        img.dataset.color = c.value;
        img.src = c.image;
        if (c.srcset) img.srcset = c.srcset;
        img.alt = c.alt || '';
      });
    }

    // ---------------------------------------------------------------- events
    bind() {
      document.addEventListener('click', (e) => {
        const t = e.target.closest('[data-silk-pick],[data-silk-size],[data-silk-add],[data-silk-remove],[data-silk-atc],[data-silk-scroll],[data-silk-open]');
        if (!t) return;
        if (t.matches('[data-silk-pick]')) {
          this.pick(Number(t.dataset.silkPick));
          if (t.dataset.silkScroll) this.scrollTo(t.dataset.silkScroll);
        } else if (t.matches('[data-silk-size]')) {
          this.setState({ nextSize: t.dataset.silkSize });
        } else if (t.matches('[data-silk-add]')) {
          this.add(t.dataset.silkAdd);
        } else if (t.matches('[data-silk-remove]')) {
          this.remove(Number(t.dataset.silkRemove));
        } else if (t.matches('[data-silk-atc]')) {
          e.preventDefault();
          this.onAtc(t);
        } else if (t.matches('[data-silk-open]')) {
          const d = qs(`details[data-silk-acc="${t.dataset.silkOpen}"]`);
          if (d) {
            d.open = true;
            d.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        } else if (t.matches('[data-silk-scroll]')) {
          e.preventDefault();
          this.scrollTo(t.dataset.silkScroll);
        }
      });
    }
    scrollTo(sel) {
      const el = qs(sel);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    onAtc(btn) {
      const { full, n, count } = this.values();
      if (!full) {
        const left = n - count;
        this.setState({ note: fill(left === 1 ? this.t.ctaMoreOne : this.t.ctaMore, { n: left }) });
        const b = qs('[data-silk-builder]');
        if (b) b.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      this.addToCart(btn);
    }
    cartItems() {
      const byId = new Map();
      this.items().slice(0, this.state.pack).forEach((i) => {
        const v = this.variant(i.size, i.color);
        if (v) byId.set(v.id, (byId.get(v.id) || 0) + 1);
      });
      return Array.from(byId, ([id, quantity]) => ({ id, quantity }));
    }
    async addToCart(btn) {
      const items = this.cartItems();
      if (!items.length) return;
      const errors = qsa('[data-silk-error]');
      errors.forEach((el) => (el.textContent = ''));
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      const drawer = document.querySelector('cart-drawer');
      const body = { items };
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        body.sections = drawer.getSectionsToRender().map((s) => s.id);
        body.sections_url = window.location.pathname;
      }
      const routes = window.routes || {};
      try {
        const res = await fetch(`${routes.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || this.t.addError);
        document.dispatchEvent(new CustomEvent('silk:added', { detail: { items, pack: this.state.pack } }));
        // the cart confirmed it: start a fresh set (same bundle, empty slots)
        this.clearSet();
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'silk-pdp', cartData: data });
        }
        if (drawer && data.sections) {
          drawer.classList.remove('is-empty');
          drawer.renderContents({ id: items[0].id, sections: data.sections });
        } else {
          window.location.href = routes.cart_url || '/cart';
        }
      } catch (err) {
        errors.forEach((el) => (el.textContent = err.message || this.t.addError));
      } finally {
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
      }
    }

    // ---------------------------------------------------------------- gallery (mobile slider; desktop is a CSS grid)
    initGalleries() {
      this.galleries = qsa('[data-silk-gallery]').map((root) => {
        const track = qs('[data-silk-track]', root);
        const dots = qsa('[data-silk-dot]', root);
        const slides = () => qsa('[data-silk-slide]', track).filter((s) => s.offsetParent !== null);
        const current = () => Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
        const go = (i) => {
          const list = slides();
          if (!list.length) return;
          const j = (i + list.length) % list.length;
          track.scrollTo({ left: j * track.clientWidth, behavior: 'smooth' });
        };
        const sync = () => {
          const i = current();
          dots.forEach((d, j) => d.setAttribute('aria-current', String(j === i)));
        };
        let raf = 0;
        track.addEventListener('scroll', () => {
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(sync);
        }, { passive: true });
        dots.forEach((d, j) => d.addEventListener('click', () => go(j)));
        const prev = qs('[data-silk-prev]', root);
        const next = qs('[data-silk-next]', root);
        if (prev) prev.addEventListener('click', () => go(current() - 1));
        if (next) next.addEventListener('click', () => go(current() + 1));
        const hero = () => Math.max(0, slides().findIndex((sl) => sl.hasAttribute('data-silk-hero')));
        return { go, hero };
      });
    }
    showSlide(i) {
      (this.galleries || []).forEach((g) => g.go(i === 'hero' ? g.hero() : i));
    }

    // ---------------------------------------------------------------- sticky bar: shows once you've scrolled past the first
    // buy area; hides again while any buy area (hero, buy box, final offer) is on screen
    initSticky() {
      const bars = qsa('[data-silk-sticky]');
      if (!bars.length) return;
      const guards = qsa('[data-silk-sticky-guard]');
      if (!guards.length) return;
      let raf = 0;
      const check = () => {
        const vh = window.innerHeight;
        const inView = guards.some((g) => {
          const r = g.getBoundingClientRect();
          return r.bottom > 0 && r.top < vh;
        });
        const past = guards[0].getBoundingClientRect().bottom < 0;
        bars.forEach((b) => b.classList.toggle('is-visible', past && !inView));
      };
      window.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(check);
      }, { passive: true });
      window.addEventListener('resize', check);
      check();
    }

    // ---------------------------------------------------------------- delivery timeline (store time zone, weekday cutoff)
    initDelivery() {
      const els = qsa('[data-silk-delivery]');
      if (!els.length) return;
      const run = () => els.forEach((el) => this.renderDelivery(el));
      run();
      setInterval(run, 30000);
    }
    renderDelivery(el) {
      const d = el.dataset;
      const cutoffHour = Number(d.cutoff || 14);
      const minDays = Number(d.min || 2);
      const maxDays = Number(d.max || 4);
      const m = /^([+-])(\d{2})(\d{2})$/.exec(d.tz || '+0000');
      const tzMin = m ? (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3])) : 0;
      // shop wall-clock time, carried in the UTC fields of a Date
      const now = new Date(Date.now() + tzMin * 60000);
      const day = (x) => new Date(Date.UTC(x.getUTCFullYear(), x.getUTCMonth(), x.getUTCDate()));
      const weekend = (x) => x.getUTCDay() === 0 || x.getUTCDay() === 6;
      const cutoff = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), cutoffHour);
      const today = !weekend(now) && now.getTime() < cutoff;
      const ship = day(now);
      if (!today) {
        do ship.setUTCDate(ship.getUTCDate() + 1);
        while (weekend(ship));
      }
      const addBiz = (x, k) => {
        const r = new Date(x);
        let j = 0;
        while (j < k) {
          r.setUTCDate(r.getUTCDate() + 1);
          if (!weekend(r)) j += 1;
        }
        return r;
      };
      const fmt = (x, o) => x.toLocaleDateString('en-US', Object.assign({ timeZone: 'UTC' }, o));
      const a1 = addBiz(ship, minDays);
      const a2 = addBiz(ship, maxDays);
      const md = { month: 'short', day: 'numeric' };
      const arrive = fmt(a1, md) + ' – ' + (a1.getUTCMonth() === a2.getUTCMonth() ? a2.getUTCDate() : fmt(a2, md));
      let l1;
      let v1;
      let v2;
      if (today) {
        const mins = Math.max(1, Math.ceil((cutoff - now.getTime()) / 60000));
        const h = Math.floor(mins / 60);
        l1 = d.orderWithin;
        v1 = h > 0 ? `${h}h ${mins % 60}m` : `${mins}m`;
        v2 = d.today;
      } else {
        const tomorrow = day(now);
        tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
        l1 = d.order;
        v1 = d.today;
        v2 = ship.getTime() === tomorrow.getTime() ? d.tomorrow : fmt(ship, { weekday: 'short', month: 'short', day: 'numeric' });
      }
      const set = (k, val) => {
        const x = qs(`[data-silk-d="${k}"]`, el);
        if (x && x.textContent !== val) x.textContent = val;
      };
      set('l1', l1);
      set('v1', v1);
      set('v2', v2);
      set('v3', arrive);
    }
  }

  document.addEventListener(
    'toggle',
    (e) => {
      const d = e.target;
      if (!(d instanceof HTMLDetailsElement) || !d.open || !d.dataset.silkGroup) return;
      qsa(`details[data-silk-group="${d.dataset.silkGroup}"]`).forEach((o) => {
        if (o !== d && o.open) o.open = false;
      });
    },
    true
  );

  // gallery MP4s from theme assets start buffering right after the page has loaded (not during it, so they don't
  // compete with the first photos), so they're ready before you swipe to them; they play only while on screen; with
  // reduced motion they stay paused on the poster
  const initVideos = () => {
    const vids = qsa('video[data-silk-video]');
    if (!vids.length || !('IntersectionObserver' in window)) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      vids.forEach((v) => { v.removeAttribute('autoplay'); v.pause(); });
      return;
    }
    // buffer after load: autoplay makes iOS actually download it; the 'playing' check below keeps it paused off screen.
    // A video that's already on screen (the desktop gallery) has started playing by then; load() would reset it and it
    // would jump back to the start, so it's left alone.
    const buffer = () => vids.forEach((v) => {
      if (!v.hasAttribute('data-silk-buffer')) return;
      v.removeAttribute('data-silk-buffer');
      v.autoplay = true;
      v.preload = 'auto';
      if (v.readyState === 0 && v.paused && !v.currentTime) v.load();
    });
    const later = () => ('requestIdleCallback' in window ? requestIdleCallback(buffer, { timeout: 2000 }) : setTimeout(buffer, 600));
    if (document.readyState === 'complete') later(); else window.addEventListener('load', later, { once: true });
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        const v = e.target;
        v.silkVisible = e.isIntersecting;
        if (e.isIntersecting) {
          const p = v.play();
          if (p && p.catch) p.catch(() => {});
        } else if (!v.paused) v.pause();
      });
    }, { threshold: 0.25 });
    vids.forEach((v) => {
      // autoplay buffers it on page load; keep it paused (on its first frame) until it's on screen
      v.addEventListener('playing', () => { if (v.silkVisible === false) v.pause(); });
      io.observe(v);
    });
  };

  // swipeable rails (shop by color, angle cards): native scroll-snap, plus arrows and a progress bar
  const initRails = () => {
    qsa('[data-silk-rail]').forEach((rail) => {
      const track = qs('[data-silk-rail-track]', rail);
      if (!track) return;
      const section = rail.closest('.silk') || rail;
      const prev = qs('[data-silk-rail-prev]', section);
      const next = qs('[data-silk-rail-next]', section);
      const bar = qs('[data-silk-rail-bar]', rail);
      const step = () => {
        const item = track.children[0];
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return item ? item.getBoundingClientRect().width + gap : track.clientWidth;
      };
      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        const scrollable = max > 2;
        rail.classList.toggle('is-static', !scrollable);
        if (bar) {
          const vis = track.clientWidth / Math.max(1, track.scrollWidth);
          bar.style.width = Math.max(12, vis * 100) + '%';
          bar.style.transform = `translateX(${max > 0 ? (track.scrollLeft / max) * ((1 - vis) / vis) * 100 : 0}%)`;
        }
        if (prev) prev.disabled = !scrollable || track.scrollLeft <= 2;
        if (next) next.disabled = !scrollable || track.scrollLeft >= max - 2;
      };
      if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      if (next) next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  };

  const start = () => {
    initVideos();
    initRails();
    const el = qs('script[data-silk-config]');
    if (!el) return;
    try {
      window.SilkPDP = new SilkPDP(JSON.parse(el.textContent));
    } catch (e) {
      console.error('[silk] could not start', e);
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
